import { DateTime } from "luxon"
import type { UseFormReturn } from "react-hook-form"
import type { TFunction } from "i18next"

import type {
  DeviceSettings,
  InstallationDetailsForm,
  InstallationType,
} from "types/device.types"
import type { Powerline } from "types/powerlines.types"
import type { Span } from "types/spans.types"
import type { GetTowerGroupedOptionsParams, Tower, TowerOption } from "types/towers.types"

import { formatDateFromMillis } from "helpers/utils/dateUtils"
import { numberToLocale, localeNumberToStandard } from "helpers/utils/localeNumber"
import { nameOrUnnamed } from "helpers/utils/translations"

export const EMPTY_FORM_STATE: InstallationDetailsForm = {
  installedAt: "",
  installationType: "",
  orientation: "",
  anchoringPointDistance: "",
  powerlineId: "",
  spanId: "",
  phaseId: "",
  towerAId: "",
  towerBId: "",
  towerId: "",
}

export const FIELD_SPLIT_THRESHOLD = 6
export const MAX_FIELDS_IN_MIN_LAYOUT = FIELD_SPLIT_THRESHOLD * 2

export const getTowerPairKey = (towerId1: number, towerId2: number): string => {
  return `${Math.min(towerId1, towerId2)}-${Math.max(towerId1, towerId2)}`
}

export const getTowerGroupedOptions = ({
  currentTower,
  otherTowerId,
  allTowers,
  spansByTowerPair,
  towersLoading,
  t,
}: GetTowerGroupedOptionsParams): TowerOption[] => {
  if (towersLoading) return []

  const otherTowerIdNum = typeof otherTowerId === "number" ? otherTowerId : undefined

  // If no towers are loaded, show only the current tower
  if (!allTowers || allTowers.length === 0) {
    if (currentTower && currentTower.id !== otherTowerIdNum) {
      return [
        {
          id: currentTower.id,
          label: `${nameOrUnnamed(currentTower.name, t)} (${currentTower.id})`,
        },
      ]
    }
    return []
  }

  // If the other tower is selected, show only towers connected to it
  if (otherTowerIdNum) {
    return allTowers
      .filter((tower) => {
        if (tower.id === otherTowerIdNum) return false
        const pairKey = getTowerPairKey(tower.id, otherTowerIdNum)
        return !!spansByTowerPair[pairKey]
      })
      .map((tower) => ({
        id: tower.id,
        label: `${nameOrUnnamed(tower.name, t)} (${tower.id})`,
        group: t("device_installation.CONNECTED_TOWERS"),
      }))
  }

  // Default: show all towers (excluding the other selected tower if present)
  return allTowers
    .filter((tower) => tower.id !== otherTowerIdNum)
    .map((tower) => ({
      id: tower.id,
      label: `${nameOrUnnamed(tower.name, t)} (${tower.id})`,
    }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const getTowerBId = (
  towerAId: number | undefined | null,
  spanTowerIds: number[] | undefined,
): number | undefined => {
  if (towerAId && spanTowerIds) {
    return spanTowerIds[0] === towerAId ? spanTowerIds[1] : spanTowerIds[0]
  }
  return
}

export const isDeviceRefetchPending = (
  updatedAt: number | undefined,
  fetchedAt: number | undefined,
): boolean =>
  updatedAt !== undefined && (fetchedAt === undefined || fetchedAt < updatedAt)

const TOWER_PAIR_FIELDS: (keyof InstallationDetailsForm)[] = [
  "towerAId",
  "towerBId",
  "spanId",
]

// Tower B is only stored through the span, so a partial pair would lose it on save
export const isTowerPairFieldMissing = (
  name: string,
  values: Partial<InstallationDetailsForm>,
): boolean =>
  TOWER_PAIR_FIELDS.some((field) => field === name && !values[field]) &&
  TOWER_PAIR_FIELDS.some((field) => values[field])

const formatTowerDisplay = (tower: Tower | undefined, t: TFunction): string => {
  return tower ? `${nameOrUnnamed(tower.name, t)} (${tower.id})` : ""
}

const formatSpanDisplay = (span: Span | undefined, t: TFunction): string => {
  return span ? `${nameOrUnnamed(span.name, t)} (${span.id})` : ""
}

const formatPowerlineDisplay = (
  powerline: Powerline | undefined,
  t: TFunction,
): string => {
  return powerline ? `${nameOrUnnamed(powerline.name, t)} (${powerline.id})` : ""
}

const getPowerlineDefaultValues = (
  deviceSettings: DeviceSettings,
  powerline: Powerline | undefined,
  span: Span | undefined,
  towerA: Tower | undefined,
  towerB: Tower | undefined,
  t: TFunction,
  locale: string,
): InstallationDetailsForm => ({
  ...EMPTY_FORM_STATE,
  installedAt: deviceSettings.installed_at
    ? formatDateFromMillis(deviceSettings.installed_at)
    : "",
  installationType: "powerline",
  orientation: deviceSettings.orientation ?? "",
  anchoringPointDistance: numberToLocale(deviceSettings.lpc_distance, locale) ?? "",
  powerlineId: formatPowerlineDisplay(powerline, t),
  spanId: formatSpanDisplay(span, t),
  phaseId: span?.phase_id ?? "",
  towerAId: formatTowerDisplay(towerA, t),
  towerBId: formatTowerDisplay(towerB, t),
})

const getTowerDefaultValues = (
  deviceSettings: DeviceSettings,
  tower: Tower | undefined,
  t: TFunction,
): InstallationDetailsForm => ({
  ...EMPTY_FORM_STATE,
  installedAt: deviceSettings.installed_at
    ? formatDateFromMillis(deviceSettings.installed_at)
    : "",
  installationType: "tower",
  powerlineId: deviceSettings.powerline_id ?? "",
  towerId: formatTowerDisplay(tower, t),
})

export const getDefaultValues = (
  deviceSettings: DeviceSettings | null | undefined,
  powerline: Powerline | undefined,
  span: Span | undefined,
  towerA: Tower | undefined,
  towerB: Tower | undefined,
  t: TFunction,
  locale = "en",
): InstallationDetailsForm => {
  if (!deviceSettings) {
    return EMPTY_FORM_STATE
  }

  switch (deviceSettings.installation_type) {
    case "powerline":
      return getPowerlineDefaultValues(
        deviceSettings,
        powerline,
        span,
        towerA,
        towerB,
        t,
        locale,
      )
    case "tower":
      return getTowerDefaultValues(deviceSettings, towerA, t)
    default:
      return EMPTY_FORM_STATE
  }
}

export const mapFormToSettings = (
  data: InstallationDetailsForm,
  locale = "en",
): Partial<DeviceSettings> => {
  const {
    installedAt,
    installationType,
    powerlineId,
    spanId,
    towerAId,
    towerId,
    orientation,
    anchoringPointDistance,
  } = data

  const parsedDate = installedAt
    ? DateTime.fromFormat(installedAt, "yyyy-MM-dd HH:mm")
    : null

  const base: Partial<DeviceSettings> = {
    installation_type: installationType as InstallationType,
    ...(parsedDate?.isValid ? { installed_at: parsedDate.toMillis() } : {}),
  }

  if (installationType === "uninstalled") {
    return {
      installation_type: "uninstalled",
      installed_at: null,
      powerline_id: null,
      span_id: null,
      tower_id: null,
      orientation: null,
      lpc_distance: null,
      clamp_type: null,
    }
  }

  if (installationType === "powerline") {
    return {
      ...base,
      powerline_id: powerlineId ? Number(powerlineId) : null,
      span_id: spanId ? Number(spanId) : null,
      tower_id: towerAId ? Number(towerAId) : null,
      orientation: orientation || null,
      lpc_distance: localeNumberToStandard(anchoringPointDistance, locale),
    }
  }

  if (installationType === "tower") {
    return {
      ...base,
      powerline_id: powerlineId ? Number(powerlineId) : null,
      tower_id: towerId ? Number(towerId) : null,
      span_id: null,
      orientation: null,
      lpc_distance: null,
      clamp_type: null,
    }
  }

  return base
}

export const getFormValues = (
  installationType: InstallationType,
  defaultValues: InstallationDetailsForm,
  powerline?: Powerline | null,
  span?: Span | null,
  towerA?: Tower | null,
  towerB?: Tower | null,
): InstallationDetailsForm => {
  if (installationType === "powerline") {
    return {
      ...defaultValues,
      powerlineId: powerline?.id ?? "",
      spanId: span?.id ?? "",
      towerAId: towerA?.id ?? "",
      towerBId: towerB?.id ?? "",
    }
  }
  if (installationType === "tower") {
    return {
      ...defaultValues,
      towerId: towerA?.id ?? "",
    }
  }
  return defaultValues
}

export const handleFieldChange = (
  name: string | undefined,
  {
    getValues,
    setValue,
    clearErrors,
  }: Pick<
    UseFormReturn<InstallationDetailsForm>,
    "getValues" | "setValue" | "clearErrors"
  >,
): void => {
  if (name === "powerlineId") {
    const currentType = getValues("installationType")
    if (currentType === "powerline") {
      setValue("towerAId", "")
      setValue("towerBId", "")
      setValue("spanId", "")
    } else if (currentType === "tower") {
      setValue("towerId", "")
    }
  }
  if (name === "towerAId") {
    setValue("towerBId", "")
    setValue("spanId", "")
  }
  if (name === "towerBId") {
    setValue("spanId", "")
  }
  if (name === "installationType") {
    setValue("installedAt", formatDateFromMillis(Date.now()))
    setValue("powerlineId", "")
    setValue("spanId", "")
    setValue("phaseId", "")
    setValue("towerAId", "")
    setValue("towerBId", "")
    setValue("towerId", "")
    setValue("orientation", "")
    setValue("anchoringPointDistance", "")
  }
  // An empty pair is valid again, so drop the errors a previous submit left
  if (!getValues(TOWER_PAIR_FIELDS).some(Boolean)) {
    clearErrors(TOWER_PAIR_FIELDS)
  }
}
